/**
 * NowPage feature component.
 *
 * The primary screen for client users. Orchestrates:
 *   1. DeterrenceHero - KPI strip at the top (with live turnaround tick)
 *   2. ConcentricZonesViz / ZoneStripViz / FloorPlanViz - center viz (live ring animations)
 *   3. ViewModeToggle - segmented control for switching the viz
 *   4. StoryCardStack - alert story cards (right rail, non-blocking)
 *   5. EventRibbon - past-2h alert dots at the bottom
 *
 * Data flow:
 *   useSites() -> picks sites[0] (Phase 3 single-site; Phase 4 adds site selection)
 *   useLiveState(siteId) -> zones + sensors for the concentric viz
 *   useDeterrenceKPIs(siteId) -> hero strip values
 *   useRecentAlerts(siteId) -> event ribbon dots
 *   useLiveRingState(siteId, liveState) -> WS-driven deviceCountPerRing + activeAlertRings
 *   useStoryCards() -> WS-driven story card stack + deterrence counter tick
 *
 * View mode persists to localStorage under 'signalsentry:viewMode'.
 *
 * Phase 4 amendment 2026-04-27: site-scoping via ?site=<uuid> query param.
 *   When the URL has ?site=<uuid> (set by the admin SitesTable "View live"
 *   link), the page selects that site instead of sites[0] and renders a
 *   slim Tactical-Gold banner at the top with the slug + an "exit scope"
 *   affordance. This is the per-site drill-in. The site lookup is done
 *   client-side against useSites(); when the param doesn't match a site
 *   (cross-org / decommissioned / typo), an honest empty state explains
 *   the API-08 upstream-auth gap rather than failing silently.
 *
 * Design spec reference: section 3
 */
import { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSites, useLiveState, useDeterrenceKPIs, useRecentAlerts } from './queries';
import { useLiveRingState } from './useLiveRingState';
import { useStoryCards } from '@/features/storycard/useStoryCards';
import { DeterrenceHero } from './DeterrenceHero';
import { ConcentricZonesViz } from './ConcentricZonesViz';

/**
 * Phase 12 Plan 12-11: lazy-load the deterrence breakdown drawer so its
 * SENTINEL string + heavy Radix Dialog body stay out of the pre-auth
 * entry chunk. The drawer downloads only when the user clicks the
 * Performance tile; the bundle-isolation guard tracks the SENTINEL
 * under LAZY_CHUNK_SENTINELS to catch regressions that eagerly import
 * the module from a non-lazy boundary.
 */
const DeterrenceBreakdownDrawer = lazy(() =>
  import('./DeterrenceBreakdownDrawer').then((m) => ({
    default: m.DeterrenceBreakdownDrawer,
  })),
);
import { ZoneStripViz } from './ZoneStripViz';
import { FloorPlanViz } from './FloorPlanViz';
import { ViewModeToggle } from './ViewModeToggle';
import { EventRibbon } from './EventRibbon';
import { StoryCardStack } from '@/features/storycard/StoryCardStack';
import { CoverageMap, useSiteZones } from '@/features/coverage-map';
import type { ViewMode } from './ViewModeToggle';
import * as styles from './NowPage.css';

/** localStorage key for view mode persistence. */
const VIEW_MODE_KEY = 'signalsentry:viewMode';

/**
 * localStorage key for the Phase 9.1 "Show coverage map" toggle that swaps
 * the default ConcentricZonesViz for the new abstract-SVG CoverageMap. The
 * default is OFF so existing /now users keep the legacy ring viz; toggling
 * on persists the preference across sessions.
 */
const SHOW_COVERAGE_MAP_KEY = 'signalsentry:now:show-map';

/**
 * NowPage: the primary /now client dashboard screen.
 *
 * Renders deterrence hero, concentric zones visualization (or alternative),
 * view mode toggle, story card stack, and past-2h event ribbon.
 *
 * When a `?site=<uuid>` query param is present, the page scopes all data
 * sources to that single site (instead of sites[0]) and shows a Tactical
 * Gold banner with an exit affordance. The page <title> updates to reflect
 * scope so browser tabs / bookmarks read clearly.
 */
export function NowPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ---------------------------------------------------------------------
  // Site scoping (Phase 4 amendment 2026-04-27)
  // ---------------------------------------------------------------------

  /**
   * Optional ?site=<uuid> query param. When set, the page scopes all
   * downstream data fetches to that single site. When absent, the page
   * falls back to sites[0] (the legacy single-site default). The state
   * is derived from the URL on every render so back/forward navigation
   * stays in sync with the displayed scope.
   */
  const scopedSiteId = searchParams.get('site');

  /**
   * Clear the ?site= scope and return to the org-wide /now view. Uses
   * `replace: true` so the back button does NOT bounce the user back into
   * the scoped view (which would feel buggy after an explicit exit).
   */
  const handleExitScope = () => {
    navigate('/now', { replace: true });
  };

  // --- Data fetching ---
  const { data: sites, isLoading: sitesLoading } = useSites();

  /**
   * Resolve the active site from the sites list. When ?site=<uuid> is set,
   * pick the matching site by site_id; otherwise fall back to sites[0].
   * scopedSite may be undefined while sites is loading OR when the param
   * does not match any site in the org's site list (cross-org typo,
   * decommissioned site, etc.) - the empty-state branch below handles it.
   */
  const site = scopedSiteId
    ? sites?.find((s) => s.site_id === scopedSiteId)
    : sites?.[0];

  const { data: liveState, isLoading: liveStateLoading } = useLiveState(site?.site_id ?? '');
  const { data: deterrence, isLoading: deterrenceLoading } = useDeterrenceKPIs(
    site?.site_id ?? '',
    'week',
  );
  const { data: alerts, isLoading: alertsLoading } = useRecentAlerts(site?.site_id ?? '', 'PT2H');

  // --- WebSocket live layer ---
  const { deviceCountPerRing, activeAlertRings } = useLiveRingState(
    site?.site_id ?? '',
    liveState,
  );

  // --- Story card system ---
  const { cards, dismiss, turnedAwayCount } = useStoryCards(site?.site_id);

  // Phase 12 Plan 12-11: Performance tile drill-in drawer state. Opens
  // when the user clicks the hero / Performance tile; closes via ESC,
  // overlay click, or a row-click navigation. siteName is read from
  // the resolved site row above so the drawer title can show the slug
  // alongside the generic 'Deterrence Events' label.
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  // --- Deterrence counter: REST baseline + WS ticks ---
  const [localTurnedAwayCount, setLocalTurnedAwayCount] = useState(0);
  useEffect(() => {
    setLocalTurnedAwayCount(turnedAwayCount);
  }, [turnedAwayCount]);

  const displayedKpis = deterrence
    ? { ...deterrence, turned_away: deterrence.turned_away + localTurnedAwayCount }
    : undefined;

  // --- View mode state (persisted to localStorage) ---
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem(VIEW_MODE_KEY) as ViewMode) ?? 'zones',
  );

  // ---------------------------------------------------------------------
  // Phase 9.1 (2026-04-29): "Show coverage map" toggle.
  //
  // When ON, the zones view-mode renders the new abstract-SVG CoverageMap
  // instead of ConcentricZonesViz. ConcentricZonesViz is preserved as the
  // default so users keep their current experience until they opt in.
  //
  // The toggle state persists in localStorage under SHOW_COVERAGE_MAP_KEY.
  // CoverageMap zones come from a per-site /api/zones poll (1 fetch/min)
  // so the toggle does not trigger any fetch when off (gating below).
  // ---------------------------------------------------------------------
  const [showCoverageMap, setShowCoverageMap] = useState<boolean>(
    () => localStorage.getItem(SHOW_COVERAGE_MAP_KEY) === 'true',
  );

  /**
   * Persist the toggle preference + flip the boolean. Called by the inline
   * checkbox toggle next to the ViewModeToggle in the viz toolbar.
   */
  const handleToggleCoverageMap = (next: boolean): void => {
    setShowCoverageMap(next);
    localStorage.setItem(SHOW_COVERAGE_MAP_KEY, next ? 'true' : 'false');
  };

  /**
   * Coverage-map zone data. Only fetched when the toggle is ON; the hook
   * accepts a null siteId to disable its polling loop. This means the 60
   * req/min budget is untouched when the toggle is off.
   */
  const coverageMapSiteId = showCoverageMap ? site?.site_id ?? null : null;
  const { zones: coverageMapZones } = useSiteZones(coverageMapSiteId);

  // ---------------------------------------------------------------------
  // Document title sync
  //
  // Updates window.document.title so the browser tab + bookmark titles
  // reflect the active scope. Skipped during sitesLoading so we don't
  // briefly render "Live: undefined".
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (sitesLoading) return;
    const title = scopedSiteId && site
      ? `Live: ${site.slug} | SignalSentry`
      : 'Live | SignalSentry';
    document.title = title;
  }, [scopedSiteId, site, sitesLoading]);

  // --- Empty state: no sites assigned ---
  if (!sitesLoading && (!sites || sites.length === 0)) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateText}>
          No sites assigned to your organization. Contact your admin.
        </p>
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // Empty state: scoped site not found OR no data
  //
  // Triggered when ?site=<uuid> is present but the site is not in the
  // user's sites list (cross-org / decommissioned / typo) OR when the
  // scoped site exists but its live-state fetch returned nothing (tunnel
  // down, per-site auth not configured, etc.). The copy is honest about
  // the API-08 upstream-auth gap currently in flight rather than throwing
  // a generic error.
  // ---------------------------------------------------------------------
  if (scopedSiteId && !sitesLoading && !site) {
    return (
      <div className={styles.page}>
        <div className={styles.scopedBanner} role="status" aria-live="polite">
          <div className={styles.scopedBannerLeft}>
            <span className={styles.scopedBannerDot} aria-hidden="true" />
            <span
              className={styles.scopedBannerLabel}
              data-testid="scoped-banner-label"
            >
              Viewing live: {scopedSiteId}
            </span>
          </div>
          <button
            type="button"
            className={styles.scopedBannerExit}
            onClick={handleExitScope}
            data-testid="scoped-banner-exit"
            aria-label="Exit site-scoped view and return to fleet"
          >
            Back to fleet
          </button>
        </div>
        <div className={styles.scopedEmpty}>
          <p
            className={styles.scopedEmptyText}
            data-testid="scoped-empty-message"
          >
            No live data for {scopedSiteId}. Tailscale tunnel may be down or
            per-site auth may not be configured. Contact admin.
          </p>
        </div>
      </div>
    );
  }

  const zones = liveState?.zones ?? [];
  const sensors = liveState?.sensors ?? [];

  return (
    <div className={styles.page}>
      {/*
        Scoped-view banner (Phase 4 amendment 2026-04-27).

        Renders only when the URL has ?site=<uuid> set AND the site was
        resolved against the user's sites list. The banner is sticky just
        below the 56px TopNav so it stays visible while the user scrolls
        through the concentric viz, story cards, and event ribbon.
      */}
      {scopedSiteId && site && (
        <div
          className={styles.scopedBanner}
          role="status"
          aria-live="polite"
          data-testid="scoped-banner"
        >
          <div className={styles.scopedBannerLeft}>
            <span className={styles.scopedBannerDot} aria-hidden="true" />
            <span
              className={styles.scopedBannerLabel}
              data-testid="scoped-banner-label"
            >
              Viewing live: {site.slug}
            </span>
          </div>
          <button
            type="button"
            className={styles.scopedBannerExit}
            onClick={handleExitScope}
            data-testid="scoped-banner-exit"
            aria-label="Exit site-scoped view and return to fleet"
          >
            Back to fleet
          </button>
        </div>
      )}

      {/* Top band: deterrence hero (live tick via turnedAwayCount) */}
      <section className={styles.heroBand} aria-label="Deterrence hero">
        <DeterrenceHero
          kpis={displayedKpis}
          isLoading={deterrenceLoading}
          turnedAwayTick={turnedAwayCount}
          onPerformanceTileClick={
            site?.site_id ? () => setDrawerOpen(true) : undefined
          }
        />
      </section>

      {/*
        Phase 12 Plan 12-11: drill-in drawer renders only when we have a
        resolved site_id AND only when first opened (drawerOpen toggles
        the mount). Mounting the drawer alongside the hero (NOT inside it)
        keeps the Dialog portal at the page root so Radix's focus
        management does not fight the hero animation. Suspense fallback is
        null because the chunk is tiny and round-trip is sub-100ms; an
        empty placeholder is preferable to a flash of skeleton.
      */}
      {site?.site_id && drawerOpen ? (
        <Suspense fallback={null}>
          <DeterrenceBreakdownDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            siteId={site.site_id}
            siteName={site.slug}
          />
        </Suspense>
      ) : null}

      {/* Center band: visualization + view mode toggle */}
      <section className={styles.vizBand} aria-label="Concentric zones visualization">
        <div className={styles.vizToolbar}>
          <ViewModeToggle
            value={viewMode}
            onChange={setViewMode}
            hasBlueprint={false}
          />
          {/*
            Phase 9.1 toggle: "Show coverage map". Custom checkbox styled as
            a SOC-palette inline switch so we ship without adding the Radix
            `react-switch` package. The hidden <input> remains the source of
            truth for keyboard + screen-reader interactions; the visual pill
            is purely decorative.
          */}
          {viewMode === 'zones' && (
            <label
              className={styles.coverageMapToggle}
              data-testid="now-coverage-map-toggle"
            >
              <input
                type="checkbox"
                role="switch"
                aria-checked={showCoverageMap}
                checked={showCoverageMap}
                onChange={(e) => handleToggleCoverageMap(e.target.checked)}
                className={styles.coverageMapToggleInput}
              />
              <span
                className={styles.coverageMapToggleTrack}
                data-checked={showCoverageMap ? 'true' : 'false'}
                aria-hidden="true"
              >
                <span className={styles.coverageMapToggleThumb} />
              </span>
              <span className={styles.coverageMapToggleLabel}>
                Show coverage map
              </span>
            </label>
          )}
        </div>

        {viewMode === 'zones' && !showCoverageMap && (
          <ConcentricZonesViz
            zones={zones}
            sensors={sensors}
            deviceCountPerRing={deviceCountPerRing}
            activeAlertRings={activeAlertRings}
          />
        )}
        {viewMode === 'zones' && showCoverageMap && (
          <div
            className={styles.coverageMapWrap}
            data-testid="now-coverage-map"
          >
            <CoverageMap
              boxes={[]}
              zones={coverageMapZones}
              height={420}
            />
          </div>
        )}
        {viewMode === 'strip' && (
          <ZoneStripViz
            zones={zones}
            sensors={sensors}
            deviceCountPerRing={deviceCountPerRing}
          />
        )}
        {viewMode === 'plan' && <FloorPlanViz />}

        {/* Loading indicator for live state */}
        {liveStateLoading && !liveState && (
          <p
            style={{
              position: 'absolute',
              bottom: '16px',
              fontFamily: 'var(--fonts-sans)',
              fontSize: '11px',
              color: 'var(--colors-text-tertiary)',
            }}
          >
            Loading live state...
          </p>
        )}
      </section>

      {/* Story card stack: right rail, non-blocking, slides from right */}
      <StoryCardStack
        cards={cards}
        onDismiss={dismiss}
        onCardClick={(alertId) => navigate(`/event/${alertId}`)}
      />

      {/* Bottom band: event ribbon */}
      <section className={styles.ribbonBand} aria-label="Event ribbon">
        <EventRibbon alerts={alerts ?? []} isLoading={alertsLoading} />
      </section>
    </div>
  );
}
